// hooks/useDecompressedTokens.ts
import { useQuery } from '@tanstack/react-query';

/**
 * Hook for retrieving and processing decompressed token data from Solana transactions
 * using Helius' compression-aware transaction API.
 */

/**
 * Represents a decompressed token with its associated metadata and amounts
 */
export interface DecompressedToken {
  /** The mint address of the token */
  mint: string;
  /** Raw on-chain amount as a bigint (integer value before decimal conversion) */
  rawAmount: bigint;
  /** Human-readable amount after applying the token's decimals */
  uiAmount: number;
  /** Number of decimal places for the token */
  decimals: number;
  /** Optional owner address of the token account */
  owner?: string;
}

/**
 * Custom hook that fetches and processes decompressed token data from a Solana transaction
 * using Helius' compression-aware API. This is particularly useful for handling compressed
 * token transfers where the actual token amounts need to be decompressed.
 *
 * @param signature - The transaction signature to fetch and process
 * @returns A React Query result containing an array of decompressed tokens, merged by mint address
 */
export function useDecompressedTokensValue(signature: string | null) {
  return useQuery<DecompressedToken[], Error>({
    queryKey: ['decompressedTokens', signature],
    enabled: !!signature,
    refetchInterval: (query) => {
      const data = query.state.data;

      // If we have no data or the first token has 0 amount, keep refetching every 2 seconds
      if (!data || data.length === 0 || (data[0] && data[0].uiAmount === 0)) {
        return 1000;
      }
      // Once we have valid data, stop refetching
      return false;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      if (!signature) return [];

      // Fetch transaction data with compression info from Helius RPC
      const res = await fetch(
        process.env.NEXT_PUBLIC_SOLANA_RPC_URL as string,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: 'getTx',
            jsonrpc: '2.0',
            method: 'getTransactionWithCompressionInfo',
            params: { signature },
          }),
        },
      );
      if (!res.ok) throw new Error(`Helius RPC error ${res.status}`);

      const { result } = await res.json();
      const info = result?.compressionInfo ?? {};

      // Combine both opened and closed token accounts from the transaction
      const accounts = [
        ...(info.openedAccounts ?? []),
        ...(info.closedAccounts ?? []),
      ];

      // Process each account to extract token data
      const rawList: DecompressedToken[] = [];
      const DECIMALS = 9; // Standard decimal places for cToken 2022

      for (const acc of accounts) {
        const td = acc?.optionalTokenData;
        if (!td?.amount) continue;

        // Convert amount to bigint for precise arithmetic
        const raw = BigInt(td.amount);
        rawList.push({
          mint: td.mint,
          rawAmount: raw,
          uiAmount: Number(raw) / 10 ** DECIMALS,
          decimals: DECIMALS,
          owner: td.owner ?? undefined,
        });
      }

      // Merge tokens by mint address to combine amounts
      const mergedMap = new Map<string, DecompressedToken>();
      for (const tok of rawList) {
        // Using mint as the key - could be extended to include owner if needed
        const key = tok.mint;
        if (!mergedMap.has(key)) {
          // Create new entry if mint hasn't been seen before
          mergedMap.set(key, { ...tok });
        } else {
          // Merge amounts for existing mint
          const existing = mergedMap.get(key)!;
          existing.rawAmount = existing.rawAmount + tok.rawAmount;
          // Recalculate UI amount after merging
          existing.uiAmount =
            Number(existing.rawAmount) / 10 ** existing.decimals;
        }
      }

      return Array.from(mergedMap.values());
    },
  });
}
